/**
 * Shared selection-breadcrumb renderer + ancestor dispatch (Tier 4 Phase 2).
 * Consumed by shared-spacing escalation controls.
 *
 * Ancestry entries come from the iframe's getAncestryForEditable():
 *   { label, tagName, eid|null, isSection?, instanceId?, domHops, ancestorDepth }
 * Dispatch prefers eid (select-by-eid, un-gated in the bridge), then the
 * section path, then raw domHops via select-ancestor. ancestorDepth is only a
 * legacy fallback — it indexes the FILTERED trail and must not be replayed as
 * raw parentElement hops.
 */

import { eventBus } from '../core/events.js';
import { bridge } from './bridge.js';

/**
 * Render the crumb trail into `container` (cleared first).
 * ctx: { instanceId, eid } — the currently selected element the trail hangs off.
 */
export function renderBreadcrumb(container, ancestry, ctx = {}) {
  if (!container) return;
  container.textContent = '';
  const list = Array.isArray(ancestry) ? ancestry : [];
  list.forEach((entry, idx) => {
    const isLast = idx === list.length - 1;
    const btn = document.createElement('button');
    btn.className = `pb-gss-crumb${isLast ? ' pb-gss-crumb-current' : ''}`;
    btn.type = 'button';
    btn.textContent = entry.label || '';
    btn.dataset.tooltip = entry.label || '';
    if (entry.eid) btn.dataset.eid = entry.eid;
    if (entry.tagName) btn.dataset.tagName = entry.tagName;
    if (entry.domHops != null) btn.dataset.domHops = String(entry.domHops);
    if (entry.ancestorDepth != null) btn.dataset.ancestorDepth = String(entry.ancestorDepth);
    const selectable = !!(
      entry.eid ||
      entry.isSection ||
      entry.domHops != null ||
      entry.ancestorDepth != null
    );
    if (!isLast && selectable) {
      btn.addEventListener('click', () => selectAncestorEntry(entry, ctx));
    } else {
      btn.disabled = true;
    }
    container.appendChild(btn);
    if (!isLast) {
      const sep = document.createElement('span');
      sep.className = 'pb-gss-crumb-sep';
      sep.textContent = '›';
      container.appendChild(sep);
    }
  });
}

/** Dispatch selection of one ancestry entry. Exported for direct reuse/tests. */
export function selectAncestorEntry(entry, ctx = {}) {
  if (!entry) return;
  if (entry.eid) {
    bridge.send('select-by-eid', {
      instanceId: ctx.instanceId,
      eid: entry.eid
    });
    return;
  }
  if (entry.isSection) {
    bridge.send('clear-selected-editable', {});
    eventBus.emit('iframe:selected-editable-cleared');
    eventBus.emit('iframe:section-clicked', { instanceId: entry.instanceId || ctx.instanceId });
    return;
  }
  if (entry.domHops != null || entry.ancestorDepth != null) {
    bridge.send('select-ancestor', {
      instanceId: ctx.instanceId,
      eid: ctx.eid,
      domHops: entry.domHops,
      ancestorDepth: entry.ancestorDepth
    });
  }
}

/**
 * Cheap change signature for a trail — lets rect-driven consumers skip
 * re-rendering identical crumbs on continuous rect updates.
 */
export function breadcrumbSignature(ancestry) {
  const list = Array.isArray(ancestry) ? ancestry : [];
  return list
    .map((e) => `${e.label || ''}|${e.eid || ''}|${e.domHops != null ? e.domHops : ''}`)
    .join('>');
}
